import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Buildings, FloppyDisk } from "@phosphor-icons/react";
import UserPicker from "@/components/UserPicker";
import LogoUploader, { CompanyLogo } from "@/components/LogoUploader";

export default function CompanyProfile() {
  const { user, can } = useAuth();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/companies").then(r => {
        if (r.data.length > 0) {
          const c = r.data[0];
          setCompany(c);
          setForm({
            ...c,
            approver_user_ids: (c.approver_user_ids && c.approver_user_ids.length > 0)
              ? c.approver_user_ids
              : (c.approver_user_id ? [c.approver_user_id] : []),
          });
        }
      });
    } else if (user?.company_id) {
      api.get(`/companies/${user.company_id}`).then(r => {
        const c = r.data;
        setCompany(c);
        setForm({
          ...c,
          approver_user_ids: (c.approver_user_ids && c.approver_user_ids.length > 0)
            ? c.approver_user_ids
            : (c.approver_user_id ? [c.approver_user_id] : []),
        });
      });
    }
  }, [user]);

  useEffect(() => {
    if (!company?.id) return;
    const params = user?.role === "super_admin" ? { company_id: company.id } : {};
    api.get("/users", { params }).then(r => setUsers(r.data.filter(u => u.active !== false))).catch(()=>{});
  }, [company, user]);

  const canEdit = can("company_profile", "write");

  const save = async () => {
    if (!company?.id) { toast.error("Sin empresa asignada"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/companies/${company.id}`, {
        name: form.name, address: form.address || "", phone: form.phone || "",
        email: form.email || "", tax_id: form.tax_id || "",
        logo_url: form.logo_url || "", description: form.description || "",
        approver_user_id: form.approver_user_id || "",
        approver_email: form.approver_email || "",
        approver_name: form.approver_name || "",
        approver_user_ids: form.approver_user_ids || [],
      });
      setCompany(r.data); toast.success("Perfil actualizado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  if (!company) return (
    <div className="text-center py-16 text-neutral-500" data-testid="no-company">
      <Buildings size={48} className="mx-auto text-neutral-300 mb-4" weight="duotone"/>
      <p>No hay empresa asignada. {user?.role === "super_admin" && "Crea una desde el panel Super Admin."}</p>
    </div>
  );

  const fld = (k) => ({ value: form[k] || "", onChange: (e) => setForm({ ...form, [k]: e.target.value }), disabled: !canEdit });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="company-profile-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Configuración</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Perfil de empresa</h1>
        <p className="text-neutral-500 mt-2">Información general que aparecerá en correos y reportes.</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="p-6 border-b border-neutral-200 flex items-center gap-5">
          <CompanyLogo url={form.logo_url} className="w-20 h-20 border border-neutral-200 shrink-0"/>
          <div className="flex-1 space-y-2">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{company.name}</h2>
              <p className="text-sm text-neutral-500">{company.tax_id || "Sin RFC"}</p>
            </div>
            <LogoUploader companyId={company.id} url={form.logo_url} canEdit={canEdit} hidePreview
              onChange={(logo_url) => { setForm(f => ({ ...f, logo_url })); setCompany(c => ({ ...c, logo_url })); }}/>
          </div>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <Field label="Nombre comercial" testid="company-name-input"><input {...fld("name")} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md disabled:bg-neutral-50"/></Field>
          <Field label="RFC / Tax ID" testid="company-taxid-input"><input {...fld("tax_id")} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md disabled:bg-neutral-50"/></Field>
          <Field label="Correo de contacto" testid="company-email-input"><input type="email" {...fld("email")} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md disabled:bg-neutral-50"/></Field>
          <Field label="Teléfono" testid="company-phone-input"><input {...fld("phone")} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md disabled:bg-neutral-50"/></Field>
          <div className="md:col-span-2">
            <Field label="Dirección" testid="company-address-input"><input {...fld("address")} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md disabled:bg-neutral-50"/></Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Descripción" testid="company-desc-input"><textarea {...fld("description")} rows={3} className="w-full px-3 py-2 border border-neutral-200 rounded-md disabled:bg-neutral-50"/></Field>
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className="mt-2 pt-5 border-t border-neutral-200">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Flujo de aprobación</div>
            <p className="text-sm text-neutral-500 mb-4">
              Selecciona uno o más usuarios aprobadores. <strong>Cualquiera de ellos</strong> podrá aprobar o rechazar documentos. Todos recibirán las notificaciones por correo.
            </p>
            <div data-testid="company-approvers-picker">
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Usuarios aprobadores</label>
              <UserPicker
                users={users}
                value={form.approver_user_ids || []}
                onChange={(ids) => setForm({ ...form, approver_user_ids: ids })}
                disabled={!canEdit}
                pageSize={10}
                testid="approvers-picker"
              />
            </div>
            {(form.approver_user_ids || []).length === 0 && (
              <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                ⚠️ Sin aprobadores configurados: ningún documento podrá ser aprobado/rechazado. Selecciona al menos uno arriba.
              </div>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="p-5 bg-neutral-50 border-t border-neutral-200 flex justify-end">
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="save-company-btn">
              <FloppyDisk size={16}/> {busy ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">{label}</label>
      {children}
    </div>
  );
}
